/**
 * Chat utility functions for message format transformation
 */

/**
 * Transform server message format to component format
 * Server format: { resourceId, roomId, senderId, content, timestamp }
 * Component format: { id, text, sender: { id, name, resourceId }, timestamp }
 *
 * @param {Object} serverMsg - Message from server
 * @param {string} currentUserId - Current user ID for determining if message is from current user
 * @param {Object} senderInfo - Optional sender information object { id, name, resourceId }
 * @returns {Object} Transformed message object
 */
export function transformServerMessage(serverMsg, currentUserId, senderInfo = null) {
  if (!serverMsg) return null

  // If senderInfo is provided, use it; otherwise create a minimal sender object from senderId
  const sender = senderInfo
    ? {
        id: senderInfo.id || senderInfo.resourceId,
        name: senderInfo.name || '',
        resourceId: senderInfo.resourceId || senderInfo.id,
      }
    : {
        id: serverMsg.senderId,
        name: '', // Will need to be populated from user context or API
        resourceId: serverMsg.senderId,
      }

  return {
    id: serverMsg.resourceId || serverMsg.id,
    text: serverMsg.content || serverMsg.text,
    sender,
    timestamp: serverMsg.timestamp,
    roomId: serverMsg.roomId,
    // Keep original fields for reference
    _original: serverMsg,
  }
}

/**
 * Transform multiple server messages
 * @param {Array} serverMessages - Array of server messages
 * @param {string} currentUserId - Current user ID
 * @param {Object} userMap - Optional map of userId -> userInfo for sender lookup
 * @returns {Array} Transformed messages
 */
export function transformServerMessages(serverMessages, currentUserId, userMap = {}) {
  if (!Array.isArray(serverMessages)) return []

  return serverMessages.map((msg) => {
    const senderInfo = userMap[msg.senderId] || null
    return transformServerMessage(msg, currentUserId, senderInfo)
  })
}

/**
 * Transform server room format to conversation format
 * Server format: { roomId, userId: [userId1, userId2], lastMessage: {...}, unreadCount: number, lastReadTimestamp: number }
 * Component format: { id, participants: [...], lastMessage: {...}, unreadCount: number, lastReadTimestamp: number, ... }
 *
 * @param {Object} serverRoom - Room from server
 * @param {string} currentUserId - Current user ID
 * @param {Object} userMap - Optional map of userId -> userInfo
 * @returns {Object} Transformed conversation object
 */
export function transformServerRoom(serverRoom, currentUserId, userMap = {}) {
  if (!serverRoom) return null

  // Get other user (not current user)
  const otherUserId = serverRoom.userId?.find((id) => id !== currentUserId)
  const otherUser = otherUserId ? userMap[otherUserId] : null

  // Transform participants
  const participants = (serverRoom.userId || []).map((userId) => {
    const userInfo = userMap[userId] || { id: userId, name: 'Unknown', resourceId: userId }
    return {
      id: userInfo.id || userInfo.resourceId,
      name: userInfo.name || 'Unknown',
      resourceId: userInfo.resourceId || userInfo.id,
    }
  })

  // Transform last message if exists
  const lastMessage = serverRoom.lastMessage ? transformServerMessage(serverRoom.lastMessage, currentUserId, userMap[serverRoom.lastMessage.senderId]) : null

  return {
    id: serverRoom.roomId,
    roomId: serverRoom.roomId,
    title: otherUser?.name || 'Chat',
    participants,
    lastMessage,
    lastMessageTime: serverRoom.lastMessage?.timestamp || null,
    unreadCount: serverRoom.unreadCount || 0,
    lastReadTimestamp: serverRoom.lastReadTimestamp || 0,
  }
}

/**
 * Transform multiple server rooms
 * @param {Array} serverRooms - Array of server rooms
 * @param {string} currentUserId - Current user ID
 * @param {Object} userMap - Optional map of userId -> userInfo
 * @returns {Array} Transformed conversations
 */
export function transformServerRooms(serverRooms, currentUserId, userMap = {}) {
  if (!Array.isArray(serverRooms)) return []

  return serverRooms.map((room) => transformServerRoom(room, currentUserId, userMap))
}
